(() => {
  const selectors = {
    wrapper: '[data-announcement-wrapper]',
    marquee: '.announcement__bar-holder--marquee',
    slide: '[data-slide]',
    slider: '[data-slider]',
    ticker: 'ticker-bar',
    tickerSlide: '.announcement__slide',
    staticSlide: 'ticker-bar[static]',
    frame: '[data-ticker-frame]',
    focusable: 'a, button',
  };

  const classes = {
    top: 'announcement__wrapper--top',
    pinned: 'announcement__wrapper--pinned',
    bodyPinned: 'has-pinned-announcement',
    selected: 'is-selected',
    entering: 'is-entering',
    leaving: 'is-leaving',
    reverse: 'is-reverse',
  };

  const attributes = {
    blockId: 'data-block-id',
  };

  const variables = {
    slideHeight: '--announcement-slide-height',
    barHeight: '--announcement-bar-height',
    desktopHeight: '--ANNOUNCEMENT-HEIGHT-DESKTOP',
    mobileHeight: '--ANNOUNCEMENT-HEIGHT-MOBILE',
  };

  if (customElements.get('announcement-bar')) return;

  customElements.define(
    'announcement-bar',
    class AnnouncementBar extends HTMLElement {
      constructor() {
        super();

        this.slider = this.querySelector(selectors.slider);
        this.slidesCount = this.querySelectorAll(selectors.tickerSlide).length;
        this.staticSlides = [...this.querySelectorAll(selectors.staticSlide)];
        this.wrapper = this.closest(selectors.wrapper);
        this.isTop = this.wrapper?.classList.contains(classes.top);
        this.isPinned = this.wrapper?.classList.contains(classes.pinned);
        this.barHeight = null;
        this.activeSlide = null;
        this.activeIndex = 0;
        this.isFocused = false;
        this.isPointerDown = false;
        this.slideHeight = null;
        this.heightFrame = null;

        this.resizeEvent = () => this.resize();
        this.changeEvent = () => this.onSlideChange();
        this.restartEvent = () => this.restartPlayer();
        this.pointerReleaseEvent = () => (this.isPointerDown = false);
        this.requestHeightEvent = () => this.requestHeight();
      }

      connectedCallback() {
        this.addEventListener('theme:slider:loaded', () => {
          this.querySelectorAll(selectors.ticker).forEach((ticker) => {
            ticker.dispatchEvent(new CustomEvent('theme:ticker:refresh'));
          });
          this.bindSlider();
          this.requestHeight();
        });

        this.addEventListener('theme:countdown:hide', (event) => {
          if (window.Shopify.designMode) return;

          if (this.slidesCount < 2) {
            this.querySelector(selectors.ticker).style.display = 'none';
          }

          if (event.target.closest(selectors.marquee)) {
            this.removeTickerText(event.target.closest(selectors.tickerSlide));
          } else {
            this.removeSlide(event.target.closest(selectors.slide));
          }
        });

        this.addEventListener('theme:countdown:expire', () => {
          this.querySelectorAll(selectors.ticker).forEach((ticker) => {
            ticker.dispatchEvent(new CustomEvent('theme:ticker:refresh'));
          });
          this.requestHeight();
        });

        this.addEventListener('theme:block:select', (event) => this.selectBlock(event.detail?.blockId));
        this.addEventListener('theme:block:deselect', () => {
          this.slider?.dispatchEvent(new CustomEvent('theme:slider:deselect', {bubbles: false}));
        });

        this.addEventListener('pointerdown', () => (this.isPointerDown = true), {capture: true});
        document.addEventListener('pointerup', this.pointerReleaseEvent, {capture: true});
        document.addEventListener('pointercancel', this.pointerReleaseEvent, {capture: true});
        this.addEventListener('focusin', (event) => {
          if (this.isPointerDown || !event.target.matches(':focus-visible')) return;
          this.isFocused = true;
          this.updatePlayer();
        });
        this.addEventListener('focusout', (event) => {
          if (this.contains(event.relatedTarget)) return;
          this.isFocused = false;
          this.updatePlayer();
        });

        document.addEventListener('theme:resize:width', this.resizeEvent);

        if (this.staticSlides.length) {
          this.resizeObserver = new ResizeObserver(this.requestHeightEvent);
          this.staticSlides.forEach((slide) => {
            const frame = slide.querySelector(selectors.frame);
            if (frame) this.resizeObserver.observe(frame);
          });
          document.fonts?.ready.then(this.requestHeightEvent);
          window.addEventListener('load', this.requestHeightEvent, {once: true});
          this.setHeight();
        }

        if (this.isTop && this.wrapper) {
          this.barObserver = new ResizeObserver(() => this.updateBarHeight());
          this.barObserver.observe(this.wrapper);
        }

        if (this.slider?.flkty) {
          this.bindSlider();
        } else {
          this.syncSlides();
        }

        document.dispatchEvent(new CustomEvent('theme:announcement:init', {bubbles: true}));
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize:width', this.resizeEvent);
        document.removeEventListener('pointerup', this.pointerReleaseEvent, {capture: true});
        document.removeEventListener('pointercancel', this.pointerReleaseEvent, {capture: true});
        this.resizeObserver?.disconnect();
        this.barObserver?.disconnect();
        this.flkty?.off('change', this.changeEvent);
        this.flkty?.off('uiChange', this.restartEvent);
        cancelAnimationFrame(this.heightFrame);
      }

      resize() {
        if (this.isTop && this.wrapper) this.updateBarHeight();
        if (!this.slider) return;

        this.slider.dispatchEvent(new CustomEvent('theme:slider:init', {bubbles: false}));
        this.slider.dispatchEvent(new CustomEvent('theme:slider:reposition', {bubbles: false}));
        this.bindSlider();
        this.requestHeight();
      }

      bindSlider() {
        const flkty = this.slider?.flkty;

        if (this.flkty && this.flkty !== flkty) {
          this.flkty.off('change', this.changeEvent);
          this.flkty.off('uiChange', this.restartEvent);
        }

        this.flkty = flkty?.isActive ? flkty : null;
        this.flkty?.off('change', this.changeEvent);
        this.flkty?.on('change', this.changeEvent);
        this.flkty?.off('pointerDown', this.flkty.stopPlayer);
        this.flkty?.off('uiChange', this.flkty.stopPlayer);
        this.flkty?.off('uiChange', this.restartEvent);
        this.flkty?.on('uiChange', this.restartEvent);
        this.activeSlide = this.flkty?.selectedElement || null;
        this.activeIndex = this.flkty?.selectedIndex || 0;
        this.updatePlayer();
        this.syncSlides();
      }

      requestHeight() {
        if (!this.staticSlides.length) return;

        cancelAnimationFrame(this.heightFrame);
        this.heightFrame = requestAnimationFrame(() => this.setHeight());
      }

      setHeight() {
        const visibleSlides = this.staticSlides.filter((slide) => slide.isConnected && slide.getClientRects().length);
        let height = 0;

        if (visibleSlides.length) {
          const minHeight = parseFloat(window.getComputedStyle(visibleSlides[0]).minHeight) || 0;
          const contentHeights = visibleSlides.map((slide) => slide.querySelector(selectors.frame)?.getBoundingClientRect().height || 0);
          height = Math.ceil(Math.max(minHeight, ...contentHeights));
        }

        if (height === this.slideHeight || !height) return;

        this.slideHeight = height;
        this.style.setProperty(variables.slideHeight, `${height}px`);
        this.flkty?.resize();
      }

      updateBarHeight() {
        const height = this.wrapper.getClientRects().length ? Math.ceil(this.wrapper.getBoundingClientRect().height) : 0;
        const root = document.documentElement;
        const variable = window.matchMedia('(max-width: 749px)').matches ? variables.mobileHeight : variables.desktopHeight;

        root.style.setProperty(variable, `${height}px`);

        if (this.isPinned) {
          root.style.setProperty(variables.barHeight, `${height}px`);
          document.body.classList.toggle(classes.bodyPinned, height > 0);
        }

        if (height === this.barHeight) return;

        this.barHeight = height;
        document.dispatchEvent(new CustomEvent('theme:pushdown:settled', {bubbles: true}));
      }

      onSlideChange() {
        const selected = this.flkty?.selectedElement;
        const index = this.flkty?.selectedIndex ?? 0;

        this.slider.classList.toggle(classes.reverse, index < this.activeIndex);
        this.activeIndex = index;

        this.querySelectorAll(`.${classes.leaving}`).forEach((slide) => slide.classList.remove(classes.leaving));

        if (this.activeSlide && this.activeSlide !== selected) {
          this.activeSlide.classList.add(classes.leaving);
        }

        if (selected) {
          selected.classList.add(classes.entering);
          void selected.offsetHeight;
          selected.classList.remove(classes.entering);
        }

        this.activeSlide = selected;
        this.syncSlides();
      }

      syncSlides() {
        const cells = this.flkty ? this.flkty.cells.map((cell) => cell.element) : [];

        this.querySelectorAll(selectors.slide).forEach((slide) => {
          const isHidden = cells.includes(slide) && !slide.classList.contains(classes.selected);

          if (isHidden) {
            slide.setAttribute('aria-hidden', 'true');
          } else {
            slide.removeAttribute('aria-hidden');
          }

          slide.querySelectorAll(selectors.focusable).forEach((element) => {
            element.setAttribute('tabindex', isHidden ? '-1' : '0');
          });
        });
      }

      restartPlayer() {
        if (!this.flkty?.options.autoPlay) return;

        this.flkty.stopPlayer();
        this.flkty.playPlayer();
        this.updatePlayer();
      }

      updatePlayer() {
        if (!this.flkty?.options.autoPlay) return;

        if (this.isFocused) {
          this.flkty.pausePlayer();
        } else {
          this.flkty.unpausePlayer();
        }
      }

      selectBlock(blockId) {
        if (!blockId || !this.flkty) return;

        const slide = this.querySelector(`[${attributes.blockId}="${blockId}"]`);
        const index = this.flkty.cells.findIndex((cell) => cell.element === slide);
        if (index < 0) return;

        this.slider.dispatchEvent(new CustomEvent('theme:slider:select', {bubbles: false, detail: {index}}));
      }

      removeSlide(slide) {
        this.slider?.dispatchEvent(new CustomEvent('theme:slider:remove-slide', {bubbles: false, detail: {slide}}));
        this.staticSlides = this.staticSlides.filter((item) => item !== slide);
        this.requestHeight();
      }

      removeTickerText(slide) {
        const ticker = slide.closest(selectors.ticker);
        slide.remove();
        ticker.dispatchEvent(new CustomEvent('theme:ticker:refresh'));
      }
    }
  );
})();
